import { useNavigation } from '@react-navigation/native';
import React, { createContext, useCallback, useEffect, useState } from 'react';

import { SignedInNavigationScreenNavigationProp } from '../../routes/types';

import { ImpersonateMenuPage } from './types';

export const ImpersonateMenuContext = createContext<ImpersonateMenuContextType>({
  isOpen: false,
  setIsOpen: () => {},
  initialPage: ImpersonateMenuPage.User,
  setInitialPage: () => {},
  queueActionAfterClose: () => {},
  confirmationCallback: undefined,
  setConfirmationCallback: () => {},
});

export interface ImpersonateMenuContextType {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean, initialPage?: ImpersonateMenuPage) => void;
  initialPage: ImpersonateMenuPage;
  setInitialPage: (initialPage: ImpersonateMenuPage) => void;
  queueActionAfterClose: (action: Function) => void;
  confirmationCallback?: () => Promise<boolean>;
  setConfirmationCallback: (callback?: () => Promise<boolean>) => void;
}

export const ImpersonateMenuProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [initialPage, setInitialPage] = useState<ImpersonateMenuPage>(ImpersonateMenuPage.User);
  const [closeQueue, setCloseQueue] = useState<Function[]>([]);
  const [confirmationCallback, setConfirmationCallback] = useState<
    (() => Promise<boolean>) | undefined
  >(undefined);
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();

  const handleIsOpen = useCallback(
    (openState: boolean, initialPageWhenOpened?: ImpersonateMenuPage) => {
      setIsOpen(openState);
      setInitialPage(initialPageWhenOpened ?? ImpersonateMenuPage.User);
    },
    []
  );

  const handleSetConfirmationCallback = useCallback((callback?: () => Promise<boolean>) => {
    setConfirmationCallback(() => callback);
  }, []);

  const queueActionAfterClose = useCallback((action: Function) => {
    setCloseQueue((prev) => [...prev, action]);
  }, []);

  useEffect(() => {
    if (closeQueue.length > 0 && !isOpen) {
      closeQueue.forEach((action) => action());
      setCloseQueue([]);
    }
  }, [closeQueue, isOpen]);

  useEffect(() => {
    if (isOpen) {
      navigation.navigate('ImpersonateMenu');
    }
  }, [isOpen, navigation]);

  return (
    <ImpersonateMenuContext.Provider
      value={{
        isOpen,
        setIsOpen: handleIsOpen,
        initialPage,
        setInitialPage,
        queueActionAfterClose,
        confirmationCallback,
        setConfirmationCallback: handleSetConfirmationCallback,
      }}>
      {children}
    </ImpersonateMenuContext.Provider>
  );
};
